import { Injectable } from "@angular/core";
import { HttpService } from "./http.service";
import { EnvService } from "./env.service";
import type { fhirR4 } from "@smile-cdr/fhirts";
import { firstValueFrom } from "rxjs";
import { DebugEventService } from "./debug-event.service";

@Injectable({
  providedIn: "root",
})
export class GroupService {
  static readonly PROVIDER_PROFILE =
    "http://hl7.org/fhir/us/davinci-pdex/StructureDefinition/pdex-provider-group";
  static readonly EXPORT_TYPE = {
    ATR: "hl7.fhir.us.davinci-atr",
    DELTA: "hl7.fhir.us.davinci-pdex#provider-delta",
    DOWNLOAD: "hl7.fhir.us.davinci-pdex#provider-download",
    SNAPSHOT: "hl7.fhir.us.davinci-pdex#provider-snapshot",
  };

  constructor(
    private readonly httpService: HttpService,
    private readonly envService: EnvService,
    private readonly debugService: DebugEventService
  ) {}

  async getGroups(): Promise<fhirR4.Group[]> {
    const payerGroupNpiSystem: string =
      this.envService.environment.payerGroupNpiSystem;
    const payerGroupNpiValue: string =
      this.envService.environment.payerGroupNpiValue;
    return (await this.loadResources(
      "Group?_profile=" +
        GroupService.PROVIDER_PROFILE +
        "&identifier=" +
        payerGroupNpiSystem +
        "|" +
        payerGroupNpiValue
    )) as fhirR4.Group[];
  }

  async getGroupWithMembers(groupId: string): Promise<fhirR4.Resource[]> {
    return await this.loadResources(
      "Group?_include=Group:member&_id=" + groupId
    );
  }

  private async loadResources(query: string) {
    const url = `${this.envService.environment.payerFhirEndpoint}/${query}`;
    const results =
      (
        await firstValueFrom(
          this.httpService.executeGETQueryWithParams<fhirR4.Bundle>(url)
        )
      ).entry?.map((entry) => entry.resource) || [];
    return results;
  }

  davinciExport(formValues){
    const url = `${this.envService.environment.payerFhirEndpoint}/Group/${formValues.group}/$davinci-data-export`;
    const params = this.buildParams(formValues);
    const headers = this.httpService
      .getTokenHeaders()
      .append("Prefer", "respond-async");

    return firstValueFrom(
      this.httpService.executePOSTFullResponse<fhirR4.Bundle>(
        url,
        params,
        headers
      )
    )
      .then((response) => {
        const jobUrl = response.headers.get("Content-Location");
        console.log(
          `DaVinci Data Export (${formValues.exportType}) job started at ${jobUrl}`
        );
        this.debugService.alert({
          category: "davinciExport",
          request: { url, body: params },
          response: jobUrl,
          type: "success",
        });
        return {...response, message: jobUrl};
      })
      .catch((err) => {
        this.debugService.alert({
          category: "davinciExport",
          request: { url, body: params },
          response: err,
          type: "error",
        });
        return err;
      });
  }

  buildParams(formValues) {
    const exportType = GroupService.EXPORT_TYPE[formValues.exportType];
    let otherParams = formValues.patients.map((id) => {
      return {
        name: "patient",
        valueReference: {
          reference: `${id}`,
        },
      };
    }) as fhirR4.ParametersParameter[];
    ["since", "until", "type", "typeFilter"].forEach((key) => {
      if (formValues[key]) {
        otherParams = otherParams.concat({
          name: `_${key}`,
          valueString:
            key === "type" || key === "typeFilter"
              ? formValues[key]
              : undefined,
          valueInstant:
            key === "since" || key === "until"
              ? new Date(formValues[key]).toJSON()
              : undefined,
        });
      }
    });
    return {
      resourceType: "Parameters",
      parameter: [
        {
          name: "exportType",
          valueString: exportType,
        },
        ...otherParams,
      ],
    } as fhirR4.Parameters;
  }
}
